import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import type { InventoryRow } from "../lib/types";
import { dateTime, number } from "../lib/format";
import { Modal } from "../components/Modal";
import { useI18n } from "../lib/i18n";

export function InventoryPage() {
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const [search, setSearch] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [selected, setSelected] = useState<InventoryRow | null>(null);
  const [delta, setDelta] = useState("0");
  const query = useQuery({
    queryKey: ["inventory", search, warehouse],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (warehouse) params.set("warehouse", warehouse);
      return api.get<{ inventory: InventoryRow[] }>(`/api/inventory?${params.toString()}`);
    }
  });
  const mutation = useMutation({
    mutationFn: () =>
      api.post("/api/inventory/adjust", {
        productId: selected?.product_id,
        warehouse: selected?.warehouse,
        delta: Number(delta),
        reason: "manual_adjustment"
      }),
    onSuccess: async () => {
      setSelected(null);
      setDelta("0");
      await queryClient.invalidateQueries({ queryKey: ["inventory"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard-summary"] });
    }
  });

  return (
    <div className="page-stack">
      <div className="toolbar-row">
        <div className="filter-row">
          <label className="search-box compact">
            <Search size={15} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("搜索 SKU 或商品")} />
          </label>
          <select value={warehouse} onChange={(event) => setWarehouse(event.target.value)}>
            <option value="">{t("全部仓库")}</option>
            <option value="深圳主仓">{t("深圳主仓")}</option>
            <option value="美国海外仓">{t("美国海外仓")}</option>
          </select>
        </div>
      </div>
      <div className="panel table-panel">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>SKU</th>
                <th>{t("商品")}</th>
                <th>{t("仓库")}</th>
                <th>{t("可用")}</th>
                <th>{t("占用")}</th>
                <th>{t("在途")}</th>
                <th>{t("安全库存")}</th>
                <th>{t("更新时间")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(query.data?.inventory ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="mono">{row.sku}</td>
                  <td><strong>{row.name}</strong><span className="table-subtext">{row.category}</span></td>
                  <td>{row.warehouse}</td>
                  <td className={row.available <= row.safety_stock ? "danger-text" : ""}>{number(row.available)}</td>
                  <td>{number(row.reserved)}</td>
                  <td>{number(row.in_transit)}</td>
                  <td>{number(row.safety_stock)}</td>
                  <td>{dateTime(row.updated_at)}</td>
                  <td>
                    <button type="button" className="table-action" onClick={() => setSelected(row)}>
                      <SlidersHorizontal size={14} />
                      {t("调整")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={Boolean(selected)} title={`${t("调整库存")} · ${selected?.sku ?? ""}`} onClose={() => setSelected(null)}>
        <div className="form-stack">
          <div className="summary-line">
            <span>{selected?.warehouse}</span>
            <strong>{t("当前可用")} {selected?.available}</strong>
          </div>
          <label className="field">
            <span>{t("调整数量，正数入库，负数出库")}</span>
            <input value={delta} onChange={(event) => setDelta(event.target.value)} type="number" />
          </label>
          {mutation.isError ? <div className="form-error">{mutation.error.message}</div> : null}
          <div className="modal-actions">
            <button type="button" className="ghost-button" onClick={() => setSelected(null)}>{t("取消")}</button>
            <button type="button" className="primary-button" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? t("提交中...") : t("提交调整")}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
